/* Native links and search are the baseline. Enhancement stays inside the Journal. */
(() => {
  if (customElements.get('dh-journal-flow')) return;
  const clamp = value => Math.max(0, Math.min(1, value));
  const normalize = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

  customElements.define('dh-journal-flow', class extends HTMLElement {
    connectedCallback() {
      if (this._dispose) return;
      const media = matchMedia('(prefers-reduced-motion: reduce)');
      const input = this.querySelector('#je-search');
      const form = this.querySelector('.je-search');
      const button = form.querySelector('button');
      const submitLabel = button.textContent;
      const status = this.querySelector('[data-journal-status]');
      const cleanup = [];
      let results, cards, heading, count, headingText, countText;
      let items = [], frame = 0, lastTime = 0, dirty = true, initial = true, alive = true;
      let request, requestId = 0, searchTimer = 0;
      const listen = (target, event, handler, options) => {
        target.addEventListener(event, handler, options);
        cleanup.push(() => target.removeEventListener(event, handler, options));
      };
      const remember = () => {
        history.replaceState({ ...history.state, dhJournal: { query: input.value, scrollY: window.scrollY } }, '', location.href);
      };
      const render = time => {
        frame = 0;
        if (!alive || media.matches || document.hidden) return;
        if (dirty) {
          for (const item of items) {
            let top = 0;
            for (let node = item.node; node; node = node.offsetParent) top += node.offsetTop;
            item.top = top; item.height = item.node.offsetHeight;
          }
          dirty = false;
        }
        const height = innerHeight, y = scrollY;
        const smoothing = 1 - Math.exp(-Math.min(64, time - (lastTime || time - 16)) / 105);
        lastTime = time;
        let unsettled = false;
        for (const item of items) {
          if (!item.height) continue;
          const top = item.top - y;
          const progress = clamp((height * .94 - top) / Math.max(1, height * .32));
          let target = item.image ? clamp((height - top) / (height + item.height)) * 2 - 1 : progress * progress * (3 - 2 * progress);
          if (initial && !item.image && top < height) target = 1;
          const outside = top > height + 100 || top + item.height < -100;
          item.current = item.current === null || outside ? target : item.current + (target - item.current) * smoothing;
          if (Math.abs(target - item.current) > .001) unsettled = true;
          else item.current = target;
          const value = item.current.toFixed(4);
          if (value !== item.previous) {
            item.node.style.setProperty(item.image ? '--journal-drift' : '--journal-in', value);
            item.previous = value;
          }
        }
        initial = false; this.dataset.journalMotion = 'true';
        if (unsettled) frame = requestAnimationFrame(render);
      };
      const schedule = () => { if (alive && !frame && !media.matches && !document.hidden) frame = requestAnimationFrame(render); };
      const invalidate = () => { dirty = true; schedule(); };
      const motionItems = () => {
        items = Array.from(this.querySelectorAll('[data-journal-image],[data-journal-arrive]')).map(node => ({ node, image: node.hasAttribute('data-journal-image'), top:0, height:0, current:null, previous:null }));
        initial = true; invalidate();
      };
      const preference = () => {
        cancelAnimationFrame(frame); frame = 0; lastTime = 0; this.dataset.journalMotion = 'false';
        for (const item of items) {
          item.node.style.removeProperty('--journal-in'); item.node.style.removeProperty('--journal-drift');
          item.current = null; item.previous = null;
        }
        initial = true; invalidate();
      };
      const bindResults = () => {
        results = this.querySelector('[data-journal-results]');
        cards = Array.from(results.querySelectorAll('[data-journal-item]')).map(node => ({ node, text: normalize(node.dataset.journalSearch || '') }));
        heading = results.querySelector('#je-stories-title'); count = results.querySelector('[data-journal-count]');
        headingText = heading?.textContent; countText = count?.textContent;
        motionItems();
      };
      const search = (announce = true) => {
        const complete = results.dataset.complete === 'true';
        button.type = complete ? 'button' : 'submit';
        button.textContent = complete ? this.dataset.labelClear : submitLabel;
        button.hidden = complete && !input.value;
        this.dataset.liveSearch = String(complete);
        if (!complete) { status.textContent = ''; return; }
        const words = normalize(input.value).split(' ').filter(Boolean);
        let visible = 0, archiveVisible = 0;
        for (const card of cards) {
          const matches = words.every(word => card.text.includes(word));
          card.node.hidden = !matches;
          if (matches) { visible++; if (card.node.classList.contains('je-story')) archiveVisible++; }
        }
        const filteredEmpty = words.length > 0 && visible === 0;
        results.querySelector('.je-archive').hidden = archiveVisible === 0 && (cards.length > 0 || filteredEmpty);
        results.querySelector('[data-journal-empty]').hidden = !filteredEmpty;
        if (heading) heading.textContent = words.length ? this.dataset.labelResults : headingText;
        if (count) count.textContent = words.length ? `${visible} ${visible === 1 ? this.dataset.labelOne : this.dataset.labelMany}` : countText;
        if (announce) status.textContent = words.length ? `${visible} ${visible === 1 ? this.dataset.labelOne : this.dataset.labelMany}` : '';
        invalidate();
      };
      const navigate = async (url, push, saved) => {
        const id = ++requestId;
        request?.abort(); request = new AbortController();
        const controller = request;
        const timeout = setTimeout(() => controller.abort(), 9000);
        results.setAttribute('aria-busy', 'true'); status.textContent = this.dataset.labelLoading;
        this.querySelectorAll('.je-topics a').forEach(link => link.classList.toggle('is-pending', new URL(link.href).pathname === url.pathname));
        try {
          const endpoint = new URL(url); endpoint.hash = ''; endpoint.searchParams.set('section_id', this.dataset.sectionId);
          const response = await fetch(endpoint, { signal: controller.signal, credentials:'same-origin', headers:{ 'X-Requested-With':'XMLHttpRequest' } });
          if (!response.ok) throw new Error('Section unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const next = doc.querySelector('[data-journal-results]');
          if (!next || !next.querySelector('.je-archive')) throw new Error('Incomplete section');
          if (!alive || id !== requestId) return;
          if (push) { remember(); url.hash = ''; history.pushState({ dhJournal:{ query: input.value, scrollY:window.scrollY } }, '', url); }
          if (saved) input.value = saved.query || '';
          next.classList.add('je-results-enter'); results.replaceWith(next);
          const incomingTopics = Array.from(doc.querySelectorAll('.je-topics a'));
          this.querySelectorAll('.je-topics a').forEach(link => {
            const incoming = incomingTopics.find(candidate => new URL(candidate.getAttribute('href'), url).pathname === new URL(link.href).pathname);
            if (incoming?.hasAttribute('aria-current')) link.setAttribute('aria-current','page');
            else link.removeAttribute('aria-current');
            link.classList.remove('is-pending');
          });
          bindResults(); search();
          if (!input.value) {
            const total = Number(results.dataset.total) || cards.length;
            status.textContent = `${total} ${total === 1 ? this.dataset.labelOne : this.dataset.labelMany}`;
          }
          if (saved && Number.isFinite(saved.scrollY)) window.scrollTo({top:saved.scrollY,behavior:'instant'});
        } catch (error) {
          if (alive && id === requestId) location.assign(url.href);
        } finally {
          clearTimeout(timeout);
          if (alive && id === requestId) results.removeAttribute('aria-busy');
        }
      };
      listen(this, 'click', event => {
        const link = event.target.closest('.je-topics a,.je-pagination a');
        if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const url = new URL(link.href);
        if (url.origin !== location.origin) return;
        event.preventDefault(); navigate(url, true);
      });
      listen(input, 'input', () => {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(() => { search(); remember(); }, 100);
      });
      listen(input, 'keydown', event => {
        if (event.key === 'Escape' && input.value && results.dataset.complete === 'true') {
          event.preventDefault(); input.value = ''; search(); remember();
        }
      });
      listen(form, 'submit', event => {
        if (results.dataset.complete === 'true') { event.preventDefault(); search(); remember(); }
      });
      listen(button, 'click', () => {
        if (results.dataset.complete === 'true') { input.value = ''; search(); remember(); input.focus(); }
      });
      listen(window, 'popstate', event => {
        if (location.pathname.startsWith(new URL(this.dataset.blogUrl, location.origin).pathname)) navigate(new URL(location.href), false, event.state?.dhJournal || {query:'',scrollY:0});
      });
      listen(window, 'scroll', schedule, {passive:true});
      listen(window, 'resize', invalidate, {passive:true});
      listen(window, 'pagehide', remember);
      listen(window, 'pageshow', () => { if (history.state?.dhJournal) input.value = history.state.dhJournal.query || ''; search(); invalidate(); });
      listen(document, 'visibilitychange', () => {
        if (document.hidden) { cancelAnimationFrame(frame); frame = 0; lastTime = 0; }
        else invalidate();
      });
      listen(media, 'change', preference);
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(invalidate); observer.observe(this);
        cleanup.push(() => observer.disconnect());
      }
      if (document.fonts) document.fonts.ready.then(() => { if (alive) invalidate(); });
      if (history.state?.dhJournal) input.value = history.state.dhJournal.query || '';
      bindResults(); search(false); preference();
      this._dispose = () => {
        alive = false; ++requestId; request?.abort(); cancelAnimationFrame(frame); clearTimeout(searchTimer);
        cleanup.forEach(dispose => dispose()); delete this._dispose;
      };
    }
    disconnectedCallback() { this._dispose?.(); }
  });
})();
